import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MessageSquare, LifeBuoy, Phone, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CATS = ["Booking", "Payment", "Cancellation", "Airport Transfer", "Driver", "Lost Property", "Refund", "Account", "Accessibility", "Corporate", "Other"];

export default function Help() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/faqs").then(({ data }) => setFaqs(data)).catch(() => {}); }, []);
  const filtered = faqs.filter((f) => (f.question + f.answer).toLowerCase().includes(q.toLowerCase()));

  const startChat = async () => {
    if (!user) { toast.info("Please log in to start a support chat"); navigate("/login"); return; }
    try { const { data } = await api.post("/conversations", { type: "support", subject: "Help Centre" }); navigate(`/messages?c=${data.id}`); }
    catch { toast.error("Could not start chat"); }
  };

  return (
    <div>
      <section className="bg-navy py-16">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <LifeBuoy size={36} className="mx-auto text-brand" />
          <h1 className="mt-4 font-heading text-3xl font-bold text-white lg:text-4xl">How can we help?</h1>
          <div className="mx-auto mt-6 flex max-w-lg items-center gap-2 rounded-xl bg-white px-4 h-12">
            <Search size={18} className="text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="help-search" placeholder="Search help articles" className="border-0 focus-visible:ring-0" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => <span key={c} className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm text-slate-600">{c}</span>)}
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-navy">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-4">
              {filtered.map((f) => (
                <AccordionItem key={f.id} value={f.id} data-testid={`help-faq-${f.id}`}>
                  <AccordionTrigger className="text-left font-medium text-navy">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <MessageSquare size={22} className="text-brand" />
              <h3 className="mt-3 font-heading font-semibold text-navy">Need immediate assistance?</h3>
              <p className="mt-1 text-sm text-slate-500">Chat with the SmartFares4U support team.</p>
              <Button className="mt-4 w-full bg-brand hover:bg-brand/90" onClick={startChat} data-testid="start-chat-btn">Start Chat</Button>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
              <AlertTriangle size={20} className="text-amber-600" />
              <p className="mt-2 text-sm text-amber-800">For genuine emergencies always dial <strong>999</strong>. SmartFares4U is a taxi booking service and not an emergency provider.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
